import { getInstance } from "../instance";
import { OperatorIdentity, operatorRequestConfig } from "../operatorHeaders";
import {
  AboCreditAdjustmentRequest,
  AboCreditAdjustmentResponse,
  AboMemberCreditsResponse,
  CREDITS_ADJUST_REFUSAL_STATUS,
  CREDITS_HISTORY_PAGE_SIZE,
  CREDITS_IDEMPOTENCY_KEY_PREFIX,
  CreditsAdjustRefusal,
  CreditsAdjustRefusalCode,
  CreditsHistoryType,
} from "./types";

/**
 * ABO view and adjustment of a member's credits (abo-member-credits). The
 * endpoints are versioned (`/v1/...`), so we use a RELATIVE path on the shared
 * `dsInstance` whose baseURL already carries `/v1` (same pattern as
 * `api/memberSubscriptions/actions.ts`).
 */
const instance = getInstance();

/**
 * The member's print balance and one page (0-based) of ledger history,
 * optionally filtered by movement type (`all` sends no filter).
 */
export const getMemberCredits = async (
  customerId: string,
  page = 0,
  limit = CREDITS_HISTORY_PAGE_SIZE,
  type: CreditsHistoryType = "all"
): Promise<AboMemberCreditsResponse> => {
  return instance
    .get<AboMemberCreditsResponse>(`/abo/customers/${customerId}/credits`, {
      params: type === "all" ? { page, limit } : { page, limit, type },
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

/**
 * Add (positive `amount`) or remove (negative) credits. DS verifies the
 * operator from the FusionAuth id token in `x-afy-operator-token` and checks
 * the `credits_adjust` role (ADR-001). The operator headers go on THIS request
 * only, never on the shared instance. Refusals are typed (see
 * `getCreditsAdjustRefusal`).
 */
export const adjustMemberCredits = async (
  customerId: string,
  body: AboCreditAdjustmentRequest,
  operator: OperatorIdentity
): Promise<AboCreditAdjustmentResponse> => {
  return instance
    .post<AboCreditAdjustmentResponse>(
      `/abo/customers/${customerId}/credits/adjustments`,
      body,
      operatorRequestConfig(operator)
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const isRefusalCode = (code: unknown): code is CreditsAdjustRefusalCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(CREDITS_ADJUST_REFUSAL_STATUS, code);

/**
 * The typed refusal behind a failed adjustment, or `null` for any other
 * failure. Recognised only when the body's `code` is a known code AND the
 * status is the one DS answers that code with. Structural on purpose: it
 * reads the axios error shape without depending on `axios.isAxiosError`.
 */
export const getCreditsAdjustRefusal = (
  error: unknown
): CreditsAdjustRefusal | null => {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) {
    return null;
  }
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const { code } = data as { code?: unknown };
  if (!isRefusalCode(code) || CREDITS_ADJUST_REFUSAL_STATUS[code] !== status) {
    return null;
  }
  return { code };
};

/** HTTP status of a failed request, or `null` when it never got an answer. */
export const getErrorStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === "number" ? response.status : null;
};

/** A random UUID v4, from `crypto.randomUUID` when the runtime offers it. */
const uuidV4 = (): string => {
  const cryptoApi: Crypto | undefined =
    typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};

/** A fresh idempotency key for one adjustment confirmation: `abo-<uuid v4>`. */
export const newCreditsIdempotencyKey = (): string =>
  `${CREDITS_IDEMPOTENCY_KEY_PREFIX}${uuidV4()}`;
